import { useEffect, useRef, useState } from "react";

import { analyzeDocument, getDocumentTypes } from "@services/document-analysis";
import { getUploadErrorMessage, validateFile } from "./document-intake-state";

const isBusy = (entry) => !!entry && (entry.phase === "uploading" || entry.phase === "analyzing");

/**
 * Estado de las cargas de un flujo de documentos (modal o asistente): una entrada por tipo con su
 * fase ("uploading" | "analyzing" | "done" | "error"), validacion previa, avance de subida y vista
 * previa de imagenes. Se reinicia cada vez que `active` pasa a verdadero.
 */
export const useDocumentUploads = (active) => {
  const [entries, setEntries] = useState({});
  const [typesByKey, setTypesByKey] = useState({});
  const previewUrls = useRef({});

  const releasePreview = (type) => {
    if (previewUrls.current[type]) {
      URL.revokeObjectURL(previewUrls.current[type]);
      delete previewUrls.current[type];
    }
  };

  const releaseAllPreviews = () => Object.keys(previewUrls.current).forEach(releasePreview);

  useEffect(() => {
    if (!active) {
      return undefined;
    }
    setEntries({});
    getDocumentTypes()
      .then((response) => {
        const map = {};
        (response.data || []).forEach((t) => {
          map[t.key] = t;
        });
        setTypesByKey(map);
      })
      .catch(() => setTypesByKey({}));
    return releaseAllPreviews;
  }, [active]);

  const setEntry = (type, patch) =>
    setEntries((prev) => ({ ...prev, [type]: { ...prev[type], ...patch } }));

  const upload = (type, file) => {
    if (!file) {
      return;
    }

    const validationError = validateFile(file, typesByKey[type]);
    if (validationError) {
      setEntry(type, { phase: "error", error: validationError, analysis: null, fileName: file.name });
      return;
    }

    releasePreview(type);
    if (file.type && file.type.startsWith("image/")) {
      previewUrls.current[type] = URL.createObjectURL(file);
    }

    setEntry(type, {
      phase: "uploading",
      progress: 0,
      error: null,
      analysis: null,
      fileName: file.name,
      previewUrl: previewUrls.current[type] || null,
    });

    analyzeDocument(type, file, {
      onUploadProgress: (event) => {
        if (!event.total) {
          return;
        }
        const progress = Math.round((event.loaded * 100) / event.total);
        // Al terminar de subir, lo que sigue es el analisis de la IA.
        setEntry(type, progress >= 100 ? { phase: "analyzing", progress } : { progress });
      },
    })
      .then((response) => setEntry(type, { phase: "done", analysis: response.data }))
      .catch((error) => setEntry(type, { phase: "error", error: getUploadErrorMessage(error) }));
  };

  const isAnyBusy = (requirements) => requirements.some((r) => isBusy(entries[r.type]));

  return { entries, typesByKey, upload, isAnyBusy };
};
